import { Component, OnInit, Input, ViewChild, AfterViewInit, SimpleChanges, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Observable, merge, of, race } from 'rxjs';
import { map, take, first } from 'rxjs/operators';

import { AlertService, DialogType, MessageSeverity } from '../../../services/alert.service';
import { AppTranslationService } from "../../../services/app-translation.service";
import { Utilities } from "../../../services/utilities";
import { ReportService } from '../../api/report.service';
import { ReportStandardGroupViewModel } from '../../model/models';
import { ReportStandardGroupDetailsComponent } from '../report-standard-group-details/report-standard-group-details.component';

@Component({
    selector: 'app-report-standard-group-list',
    templateUrl: './report-standard-group-list.component.html',
    styleUrls: ['./report-standard-group-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ReportStandardGroupListComponent implements OnInit {

    @Input() id: number;
    @Input() reportIsDirty: boolean = false;
    @Input() dirtyMessage: string;
    @Input() hasPermissionToChange$: Observable<boolean>;
    @Output() refreshRequest: EventEmitter<any> = new EventEmitter();

    selectedReportStandardGroupId: number;

    standardGroups$: Observable<ReportStandardGroupViewModel[]>;

    loadingIndicator: boolean;

    constructor(
        private alertService: AlertService,
        private translationService: AppTranslationService,
        private reportService: ReportService,
        private snackBar: MatSnackBar,
        public dialog: MatDialog) {

    }

    ngOnInit() {
        this.loadData();
    }

    public loadData() {
        this.alertService.startLoadingMessage();
        this.loadingIndicator = true;

        this.standardGroups$ = this.reportService.getStandardGroupsByReportId(this.id);
        this.standardGroups$.subscribe(
            success => {
                this.alertService.stopLoadingMessage();
                this.loadingIndicator = false;
            },
            error => this.onDataLoadFailed(error)
        );
    }

    private onDataLoadFailed(error: any) {
        this.alertService.stopLoadingMessage();
        this.loadingIndicator = false;

        this.alertService.showStickyMessage(this.translationService.getTranslation("Error"),
            this.translationService.getTranslation("FetchErrorMessage") +
            "\r\n" +
            Utilities.getHttpResponseMessage(error),
            MessageSeverity.error,
            error);
    }

    // Refresh request from child component, pass to parent
    public refreshRequested(reportStandardGroupId: number): void {
        this.selectedReportStandardGroupId = reportStandardGroupId;
        this.refreshRequest.emit();
        this.loadData();
    }

    public showDetails(event: Event, reportStandardGroup: ReportStandardGroupViewModel): void {
        this.okToOpenDialog().subscribe((ok) => {
            if (ok) {
                let config = new MatDialogConfig();
                config.data = {
                    reportStandardGroupId: reportStandardGroup.reportStandardGroupId,
                    reportStandardGroupName: reportStandardGroup.name,
                    reportId: reportStandardGroup.reportId,
                    hasPermissionToChange$: this.hasPermissionToChange$
                };
                //config.minWidth = "900px";
                config.disableClose = true;
                event.stopPropagation();

                let dialogRef = this.dialog.open(ReportStandardGroupDetailsComponent, config);
                dialogRef.afterClosed().subscribe(result => {
                    if (result) {
                        this.loadData();
                    }
                });
            }
        });
    }

    // Prevent opening of child dialogs if the form is dirty
    okToOpenDialog(): Observable<boolean> {
        this.alertService.resetStickyMessage();

        if (this.reportIsDirty) {
            let snackbar = this.snackBar.open(this.dirtyMessage,
                this.translationService.getTranslation("commands.OK"),
                { duration: 5000 });

            let close = snackbar.onAction().pipe(map(() => { return true; }));
            let remain = snackbar.afterDismissed().pipe(map(() => { return false; }));

            return race(close, remain);
        } else {
            return of(true);
        }
    }

}
